import { useState, useCallback } from "react";
import { Mail, Save, X } from "lucide-react";

/**
 * useSaveOptions
 * Same drop-in pattern as useConfirm(), but resolves to one of three
 * outcomes instead of true/false:
 *   "email"  - user picked "Send via Email to Admin"
 *   "draft"  - user picked "Save Draft"
 *   null     - user dismissed the dialog without choosing
 *
 * Usage:
 *   const { promptSaveOptions, saveOptionsState, handlePickEmail, handlePickDraft, handleDismiss } = useSaveOptions();
 *   const choice = await promptSaveOptions();
 *   if (choice === "email") { ... }
 *   else if (choice === "draft") { ... }
 *
 * Render <SaveOptionsDialog saveOptionsState={saveOptionsState} onPickEmail={handlePickEmail}
 *   onPickDraft={handlePickDraft} onDismiss={handleDismiss} /> once, near the top of the
 * component's returned JSX.
 */
export function useSaveOptions() {
  const [saveOptionsState, setSaveOptionsState] = useState(null); // { resolve }

  const promptSaveOptions = useCallback(() => {
    return new Promise((resolve) => {
      setSaveOptionsState({ resolve });
    });
  }, []);

  const resolveWith = useCallback((value) => {
    setSaveOptionsState((current) => {
      current?.resolve(value);
      return null;
    });
  }, []);

  const handlePickEmail = useCallback(() => resolveWith("email"), [resolveWith]);
  const handlePickDraft = useCallback(() => resolveWith("draft"), [resolveWith]);
  const handleDismiss = useCallback(() => resolveWith(null), [resolveWith]);

  return { promptSaveOptions, saveOptionsState, handlePickEmail, handlePickDraft, handleDismiss };
}

/**
 * SaveOptionsDialog - centered modal offering the two save actions.
 * Render this once per page, alongside the useSaveOptions() hook.
 */
export default function SaveOptionsDialog({ saveOptionsState, onPickEmail, onPickDraft, onDismiss }) {
  if (!saveOptionsState) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-bold text-slate-700 leading-snug">How would you like to save this OPF?</p>
          <button
            onClick={onDismiss}
            aria-label="Close"
            className="shrink-0 text-slate-400 hover:text-slate-600 transition"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-2.5">
          <button
            onClick={onPickEmail}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-bold bg-[#1e3a5f] text-white hover:bg-[#16304d] transition"
          >
            <Mail size={16} />
            Send via Email to Admin
          </button>
          <button
            onClick={onPickDraft}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
          >
            <Save size={16} />
            Save Draft
          </button>
        </div>
      </div>
    </div>
  );
}